import { Pencil, X } from 'lucide-react';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TableActionMenu, type TableActionItem } from './TableActionMenu';

export type BackofficePageMode = 'view' | 'edit';

export function useBackofficePageMode() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageMode: BackofficePageMode = searchParams.get('mode') === 'edit' ? 'edit' : 'view';

  const setPageMode = useCallback(
    (nextMode: BackofficePageMode) => {
      const next = new URLSearchParams(searchParams);
      if (nextMode === 'edit') {
        next.set('mode', 'edit');
      } else {
        next.delete('mode');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  return { pageMode, setPageMode };
}

export interface BackofficePageModeActionMenuProps {
  pageMode: BackofficePageMode;
  onPageModeChange: (pageMode: BackofficePageMode) => void;
  viewActions?: TableActionItem[] | undefined;
  fixedInlineActions?: TableActionItem[] | undefined;
  editActionLabel?: string | undefined;
  label?: string | undefined;
  containerClassName?: string | undefined;
}

export function BackofficePageModeActionMenu({
  pageMode,
  onPageModeChange,
  viewActions = [],
  fixedInlineActions = [],
  editActionLabel = 'Edit view',
  label = 'Page actions',
  containerClassName,
}: BackofficePageModeActionMenuProps) {
  if (pageMode === 'edit') {
    return (
      <button
        type="button"
        className="focus-ring inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border border-backoffice-500 bg-backoffice-500 px-3 text-small font-medium text-white hover:bg-backoffice-600"
        onClick={() => onPageModeChange('view')}
      >
        <X className="h-4 w-4" aria-hidden="true" />
        Close edit mode
      </button>
    );
  }

  const actions: TableActionItem[] = [
    ...fixedInlineActions,
    ...viewActions,
    {
      id: 'edit-mode',
      label: editActionLabel,
      icon: Pencil,
      placement: 'menu',
      onSelect: () => onPageModeChange('edit'),
    },
  ];

  return (
    <TableActionMenu
      actions={actions}
      label={label}
      {...(containerClassName ? { containerClassName } : {})}
    />
  );
}
